import { useState } from 'react'
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  InputNumber,
  Message,
  Popconfirm,
  Typography,
} from '@arco-design/web-react'
import { IconPlus, IconRefresh, IconDelete } from '@arco-design/web-react/icon'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getRedemptionsApi,
  createRedemptionApi,
  deleteRedemptionApi,
  type Redemption,
} from '@/api/redemption'
import PageHeader from '@/components/Layout/PageHeader'
import { renderTimestamp, Pill } from '@/utils/render'
import { renderQuota, quotaToDisplayAmount, displayAmountToQuota } from '@/utils/quota'
import { getFormValidateErrorMessage } from '@/utils/helpers'

export default function RedemptionPage() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [modalOpen, setModalOpen] = useState(false)
  const [form] = Form.useForm<{ name: string; quota_display: number; count: number }>()
  const [resultKeys, setResultKeys] = useState<string[]>([])
  const [resultOpen, setResultOpen] = useState(false)

  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ['redemptions', { page, pageSize }],
    queryFn: () => getRedemptionsApi({ p: page, size: pageSize }),
  })
  const createMut = useMutation({
    mutationFn: createRedemptionApi,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['redemptions'] }),
  })
  const deleteMut = useMutation({
    mutationFn: deleteRedemptionApi,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['redemptions'] }),
  })

  const handleCreate = async () => {
    try {
      const v = await form.validate()
      const data = await createMut.mutateAsync({
        name: v.name,
        quota: displayAmountToQuota(v.quota_display),
        count: v.count,
      })
      setResultKeys((data || []).map((r) => r.key).filter(Boolean))
      setResultOpen(true)
      setModalOpen(false)
      Message.success(`已生成 ${data?.length || 0} 个兑换码`)
    } catch (err: unknown) {
      Message.error(getFormValidateErrorMessage(err))
    }
  }

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    { title: '名称', dataIndex: 'name', width: 180 },
    {
      title: '额度',
      dataIndex: 'quota',
      width: 140,
      render: (v: number) => renderQuota(v),
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (v: number) =>
        v === 1 ? <Pill variant="success">未使用</Pill> : <Pill variant="neutral">已使用</Pill>,
    },
    {
      title: '使用用户',
      dataIndex: 'used_user_id',
      width: 100,
      render: (v?: number) => v || '-',
    },
    {
      title: '创建时间',
      dataIndex: 'created_time',
      width: 170,
      render: renderTimestamp,
    },
    {
      title: '兑换时间',
      dataIndex: 'redeemed_time',
      width: 170,
      render: (v?: number) => renderTimestamp(v),
    },
    {
      title: '操作',
      fixed: 'right' as const,
      width: 120,
      render: (_: unknown, row: Redemption) => (
        <Space size={2}>
          <Button
            size="small"
            type="text"
            onClick={() => {
              navigator.clipboard.writeText(row.key)
              Message.success('已复制')
            }}
          >
            复制
          </Button>
          <Popconfirm title="确认删除？" onOk={() => deleteMut.mutateAsync(row.id).then(() => Message.success('已删除'))}>
            <Button size="small" type="text" status="danger" icon={<IconDelete />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title="兑换码"
        subtitle="生成与管理兑换码"
        actions={
          <Space>
            <Button icon={<IconRefresh />} onClick={() => list.refetch()}>
              刷新
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={() => setModalOpen(true)}>
              生成兑换码
            </Button>
          </Space>
        }
      />
      <Card className="!rounded-2xl" bordered={false}>
        <Table
          rowKey="id"
          loading={list.isLoading}
          columns={columns}
          data={list.data?.items || []}
          scroll={{ x: 1200 }}
          pagination={{
            current: page,
            pageSize,
            total: list.data?.total || 0,
            showTotal: true,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
          }}
        />
      </Card>

      <Modal
        title="生成兑换码"
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleCreate}
        confirmLoading={createMut.isPending}
      >
        <Form form={form} layout="vertical" initialValues={{ count: 1, quota_display: quotaToDisplayAmount(500000) }}>
          <Form.Item field="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item field="quota_display" label="面额">
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
          <Form.Item field="count" label="生成数量" rules={[{ required: true }]}>
            <InputNumber min={1} max={100} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="生成的兑换码"
        visible={resultOpen}
        onCancel={() => setResultOpen(false)}
        footer={
          <Button
            onClick={() => {
              navigator.clipboard.writeText(resultKeys.join('\n'))
              Message.success('已复制全部')
            }}
          >
            复制全部
          </Button>
        }
      >
        <Typography.Paragraph>
          <pre className="bg-gray-100 rounded p-3 text-xs whitespace-pre-wrap break-all max-h-80 overflow-auto">
            {resultKeys.join('\n')}
          </pre>
        </Typography.Paragraph>
      </Modal>
    </div>
  )
}
